import {
  createContext,
  useContext,
  useId,
  useInsertionEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useIsSsr } from "../use-is-ssr";

const CssInstanceContext = createContext<
  | {
      dangerouslyAllowUnknownProperties?: boolean;
      inject: ({ id, rules }: { id: string; rules: string[] }) => void;
    }
  | undefined
>(undefined);

export const InternalCssInstance = ({
  children,
  value: { dangerouslyAllowUnknownProperties, nonce },
}: {
  value: {
    dangerouslyAllowUnknownProperties?: boolean;
    nonce?: string;
  };
  children: ReactNode;
}) => {
  const isSsr = useIsSsr();
  const styleElementId = useId();
  const [instance] = useState(() => createInstance());

  useInsertionEffect(() => {
    instance.setup({ nonce, styleElementId, isSsr });
    return () => {
      instance.cleanup();
    };
  }, [instance, nonce]);

  return (
    <CssInstanceContext.Provider
      value={{
        dangerouslyAllowUnknownProperties,
        inject: instance.inject,
      }}
    >
      {children}
    </CssInstanceContext.Provider>
  );
};

export const useInjectRules = () => {
  const context = useContext(CssInstanceContext);

  if (context === undefined) {
    throw new Error("useInjectRules must be used within a CssInstance");
  }

  const { dangerouslyAllowUnknownProperties, inject } = context;
  const rulesToBeInjected = useRef(new Map<string, string[]>());

  useInsertionEffect(() => {
    for (const [id, rules] of rulesToBeInjected.current) {
      inject({ id, rules });
    }
    rulesToBeInjected.current.clear();
  });

  return {
    dangerouslyAllowUnknownProperties,
    injectRules: (id: string, rules: string[]) => {
      rulesToBeInjected.current.set(id, rules);
    },
  };
};

const getRuleName = (rule: CSSRule) => {
  if (rule.constructor.name === "CSSKeyframesRule") {
    return (rule as CSSKeyframesRule).name;
  }

  if (rule.constructor.name === "CSSFontFaceRule") {
    const { style } = rule as CSSStyleRule;
    return style.fontFamily;
  }

  if (rule.constructor.name === "CSSStyleRule") {
    const { selectorText } = rule as CSSStyleRule;
    return selectorText.replace(/^\./, "");
  }

  return undefined;
};

export const parseRules = (styleElement: HTMLStyleElement): string[] => {
  const stylesheet = (styleElement as HTMLStyleElement).sheet;
  const cssRules = stylesheet?.cssRules || [];
  const cssIds: string[] = [];

  for (let i = 0; i < cssRules.length; i++) {
    const rule = cssRules[i];
    const id = getRuleName(rule!);

    if (id) {
      cssIds.push(id);
    }
  }

  return cssIds;
};

const createInstance = () => {
  let installed = false;
  let element: HTMLStyleElement | undefined;

  const injectedIds = new Set<string>();
  const rulesToBeInjected = new Set<string>();

  const setupSsr = ({ styleElementId }: { styleElementId?: string }) => {
    const styleElement = styleElementId
      ? (document.getElementById(styleElementId) as HTMLStyleElement)
      : null;

    if (!styleElement) {
      throw Error("Style element not found");
    }

    const cssIds = parseRules(styleElement);
    cssIds.forEach((id) => {
      injectedIds.add(id);
    });

    element = styleElement;
  };

  const setupBrowserOnly = ({ nonce }: { nonce?: string }) => {
    element = document.createElement("style");
    if (nonce) {
      element.nonce = nonce;
    }
    document.head.append(element);

    rulesToBeInjected.forEach((rule) => {
      element!.sheet?.insertRule(rule);
    });
  };

  return {
    setup: ({
      isSsr,
      nonce,
      styleElementId,
    }: {
      isSsr: boolean;
      nonce?: string;
      styleElementId?: string;
    }) => {
      if (installed) {
        throw new Error("Style element already injected");
      }

      if (isSsr) {
        setupSsr({ styleElementId });
      } else {
        setupBrowserOnly({ nonce });
      }

      rulesToBeInjected.clear();
      installed = true;
    },
    cleanup: () => {
      rulesToBeInjected.clear();
      injectedIds.clear();

      element!.remove();
      element = undefined;

      installed = false;
    },
    inject: ({ id, rules }: { id: string; rules: string[] }) => {
      if (injectedIds.has(id)) {
        return;
      }

      for (const rule of rules) {
        if (installed) {
          element!.sheet!.insertRule(rule);
        } else {
          rulesToBeInjected.add(rule);
        }
      }

      injectedIds.add(id);
    },
  };
};

export const InternalCssExtractor = ({
  children,
}: {
  children: ReactNode;
  callback: (arg: {
    renderStyle: () => ReactNode;
    cleanup: () => void;
  }) => void;
}) => {
  return children;
};
